import { useState } from "react";
import { History, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Chip, Empty, Field, NSelect, Panel, Plate, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import ImportWidget from "@/components/ImportWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, download, errMsg, fDate, fNum } from "@/lib/api";
import { Download } from "lucide-react";

export const HistBadge = ({ w }) => (w.source === "historique"
  ? <Chip testid="perso-hist-badge" cls="bg-violet-50 text-violet-700 border-violet-200">Historique importé / saisi manuellement</Chip>
  : null);

function HistDialog({ h, onClose, onDone }) {
  const users = useFetch(h?.id ? null : "/users", { role: "commercial", include_archived: true, with_m2: true });
  const vehs = useFetch(h?.id ? null : "/vehicles", { type: "commercial" });
  const [f, setF] = useState({ user_id: "", vehicle_id: "", friday_date: "", perso_km: "", period_label: "", comment: "", source: "Saisie manuelle", ...h });
  const [dup, setDup] = useState(null);
  const set = (k) => (e) => { setF({ ...f, [k]: e?.target ? e.target.value : e }); setDup(null); };
  const run = async () => {
    const body = { ...f, perso_km: Math.round(Number(String(f.perso_km).replace(",", "."))), period_label: f.period_label || null };
    try {
      if (h?.id) await api.patch(`/perso-history/${h.id}`, body);
      else await api.post("/perso-history", { ...body, replace: !!dup });
      toast.success("Historique enregistré");
      onDone();
    } catch (e) {
      if (e?.response?.status === 409) setDup(e.response.data.detail.msg);
      else toast.error(errMsg(e));
    }
  };
  return (
    <ConfirmDialog title={h?.id ? "Corriger un historique de KM personnels" : "Ajouter un historique de KM personnels"} onConfirm={run} onClose={onClose}
      label={dup ? "Remplacer la valeur existante" : "Enregistrer"} testid="perso-hist-dialog" primary>
      {h?.id ? <p className="text-sm"><b>{h.user_name}</b> · <Plate>{h.plate}</Plate> · vendredi {fDate(h.friday_date)} · valeur initiale {fNum(h.initial_value)} km</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Commercial *"><NSelect value={f.user_id} onChange={set("user_id")} placeholder="Choisir…" testid="perso-hist-user" options={(users.data || []).filter((u) => !u.is_demo).map((u) => ({ value: u.id, label: u.name }))} /></Field>
          <Field label="Véhicule *"><NSelect value={f.vehicle_id} onChange={set("vehicle_id")} placeholder="Choisir…" testid="perso-hist-vehicle" options={(vehs.data || []).filter((v) => !v.is_demo).map((v) => ({ value: v.id, label: v.plate }))} /></Field>
          <Field label="Date du vendredi concerné *"><Input type="date" value={f.friday_date} onChange={set("friday_date")} data-testid="perso-hist-date" /></Field>
          <Field label="Période / semaine"><Input value={f.period_label || ""} onChange={set("period_label")} placeholder="ex. S42 2026" data-testid="perso-hist-period" /></Field>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="KM personnels *"><Input inputMode="numeric" value={f.perso_km} onChange={set("perso_km")} data-testid="perso-hist-km" /></Field>
        <Field label="Source / origine *"><Input value={f.source} onChange={set("source")} placeholder="ex. Historique Excel" data-testid="perso-hist-source" /></Field>
        <Field label="Type d'usage personnel"><NSelect value={f.usage_type || ""} onChange={set("usage_type")} placeholder="Historique (non précisé)" testid="perso-hist-usage" options={[{ value: "weekend", label: "Week-end" }, { value: "conges", label: "Congés" }, { value: "autre", label: "Autre" }]} /></Field>
      </div>
      <Field label="Commentaire"><Input value={f.comment} onChange={set("comment")} data-testid="perso-hist-comment" /></Field>
      {dup && <p className="text-sm rounded-lg border border-amber-300 bg-amber-50 text-amber-800 p-2" data-testid="perso-hist-dup">{dup}</p>}
    </ConfirmDialog>
  );
}

function HistTable({ rows, onEdit, onDelete }) {
  if (!rows.length) return <Empty text="Aucun historique saisi ou importé" />;
  return (
    <div className="overflow-x-auto max-h-[480px]"><table className="tbl w-full" data-testid="perso-hist-table">
      <thead className="sticky top-0"><tr><th>Vendredi</th><th>Commercial</th><th>Véhicule</th><th className="text-right">Km perso</th><th>Source</th><th>Commentaire</th><th>Traçabilité</th><th></th></tr></thead>
      <tbody>{rows.map((h) => (
        <tr key={h.id} data-testid={`perso-hist-row-${h.id}`}>
          <td>{fDate(h.friday_date)}{h.period_label ? <span className="text-xs text-slate-400"> · {h.period_label}</span> : null}</td>
          <td className="font-semibold">{h.user_name}</td>
          <td><Plate>{h.plate}</Plate></td>
          <td className="text-right font-bold tabular-nums">{fNum(h.perso_km)}{h.initial_value !== h.perso_km && <span className="block text-[10px] text-slate-400">initial : {fNum(h.initial_value)}</span>}</td>
          <td className="text-xs">{h.source}</td>
          <td className="text-xs whitespace-normal">{h.comment || "—"}</td>
          <td className="text-[11px] text-slate-500 whitespace-normal" title={(h.history || []).map((x) => `${fDate(x.at)} ${x.by} : ${x.note}${Object.keys(x.changes || {}).length ? " " + Object.entries(x.changes).map(([k, [a, b]]) => `${k} ${a ?? "—"}→${b}`).join(", ") : ""}`).join("\n")}>
            Saisi le {fDate(h.created_at)} par {h.created_by}{h.updated_at ? ` · modifié le ${fDate(h.updated_at)} par ${h.updated_by}` : ""}
          </td>
          <td className="whitespace-nowrap">
            <Button size="sm" variant="outline" onClick={() => onEdit(h)} data-testid={`perso-hist-edit-${h.id}`}><Pencil className="w-3.5 h-3.5" /></Button>{" "}
            <Button size="sm" variant="outline" onClick={() => onDelete(h)} className="text-rose-600" data-testid={`perso-hist-delete-${h.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
          </td>
        </tr>
      ))}</tbody>
    </table></div>
  );
}

export function PersoHistoryAdmin({ onChanged }) {
  const { data, reload } = useFetch("/perso-history");
  const [open, setOpen] = useState(null);
  const [del, setDel] = useState(null);
  const [replace, setReplace] = useState(false);
  const done = () => { setOpen(null); setDel(null); reload(); onChanged(); };
  const remove = async () => {
    try {
      await api.delete(`/perso-history/${del.id}`);
      toast.success("Historique supprimé (trace conservée)");
      done();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6 mt-6 scroll-mt-4" id="perso-history-admin" data-testid="perso-history-admin">
      <Panel title="Historique saisi / importé (avant la mise en service)" flush testid="perso-hist-panel"
        action={<Button size="sm" onClick={() => setOpen({})} className="bg-[#0E2D43] hover:bg-[#163f5e]" data-testid="add-perso-history"><Plus className="w-4 h-4 mr-1" /> Ajouter un historique de KM personnels</Button>}>
        <HistTable rows={data || []} onEdit={setOpen} onDelete={setDel} />
      </Panel>
      <div className="max-w-4xl space-y-3">
        <button onClick={() => download("/imports/template/km_perso_historique", { fmt: "xlsx" }, "modele-historique-km-perso.xlsx")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#E0830B] hover:underline" data-testid="download-perso-template">
          <Download className="w-4 h-4" /> Télécharger le modèle (Excel)
        </button>
        <label className="flex items-center gap-2 text-sm text-slate-700" data-testid="perso-import-replace">
          <Checkbox checked={replace} onCheckedChange={(v) => setReplace(!!v)} data-testid="perso-import-replace-checkbox" /> Remplacer les doublons existants (même commercial + véhicule + vendredi) — je confirme le remplacement
        </label>
        <ImportWidget endpoint="/perso-history/import" params={{ replace }} title="Import en masse de l'historique (Excel / CSV)"
          hint="Colonnes : Commercial, Immatriculation, Date du vendredi concerné, KM personnels, Commentaire, Source. Les week-ends déjà calculés par les relevés de l'application sont ignorés. Analysez d'abord le fichier : les doublons sont listés et ne sont remplacés que si la case ci-dessus est cochée."
          previewCols={["user_name", "plate", "friday_date", "perso_km", "existing", "source", "comment"]} onDone={done} testid="perso-hist-import" />
      </div>
      {open && <HistDialog h={open.id ? open : null} onClose={() => setOpen(null)} onDone={done} />}
      {del && (
        <ConfirmDialog title="Supprimer cet historique ?" onConfirm={remove} onClose={() => setDel(null)} label="Supprimer" testid="perso-hist-delete-dialog">
          <p>{del.user_name} · {del.plate} · vendredi {fDate(del.friday_date)} · {fNum(del.perso_km)} km. La valeur ne sera plus comptée ; la trace de la saisie et de la suppression est conservée.</p>
        </ConfirmDialog>
      )}
      <p className="text-xs text-slate-500 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Les relevés photo vendredi / lundi restent inchangés pour les nouvelles données.</p>
    </div>
  );
}
